import {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {claimTask} from '../../api/tasks';
import ConfirmationPopup from '../common/popup/ConfirmationPopup';
import {useAppSelector} from '../../store/hooks';
import {SuccessClaimCallback, TaskToClaim} from './types';
import {useSnackbarHook} from '../../hooks/useSnackbar';

type ConfirmationClaimPopupProps = {
  closePopup: () => void,
  zoomToClaimedTask?: () => void,
  task: Nullable<TaskToClaim>,
  successClaimCallback: SuccessClaimCallback
}

const ConfirmationClaimPopup = ({
  closePopup,
  task,
  successClaimCallback,
  zoomToClaimedTask
}: ConfirmationClaimPopupProps) => {
  const {t} = useTranslation('tasks');
  const [isLoading, setIsLoading] = useState(false);
  const {firstName, lastName, id} = useAppSelector((state) => state.user);
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  const handleClaimTask = useCallback(async () => {
    if (!task || !firstName || !lastName || !id) {
      return;
    }

    setIsLoading(true);

    try {
      await claimTask(task.id);

      successClaimCallback({
        taskId: task.id,
        processId: task.processId,
        assignee: {
          firstName,
          lastName,
          id
        }
      });

      if (zoomToClaimedTask) {
        zoomToClaimedTask();
      }
      addSuccessToast(t('claim.claimTaskToastMessage'));
    } catch (e) {
      addErrorToast(e?.message);
    } finally {
      setIsLoading(false);
      closePopup();
    }
  }, [task, closePopup]);

  return (
    <ConfirmationPopup
      labels={{
        confirmBtn: t('claim.btn'),
        body: task?.isClaimed ? t('claim.claimedTaskMessage') : t('claim.notClaimedTaskMessage')
      }}
      isOpen={!!task}
      setIsOpen={closePopup}
      confirmableAction={handleClaimTask}
      isActionProcessing={isLoading}
    />
  );
};

export default ConfirmationClaimPopup;
